/**
 * Utility methods for interacting with browser storage
 */

/**
 * Get the appropriate window storage object based on the storageType value. The default type is
 * localStorage.
 *
 * @return window.sessionStorage if the requestd type is 'session'. window.localStorage for all
 *         other values (including undefined)
 */
const _getStorageObject = (storageType) => {
	return storageType === 'session' ? window.sessionStorage : window.localStorage;
};

/**
 * Retrieve a stored value (parsed as JSON) from the desired storage object.
 * Any errors accessing or parsing the value are supressed and `null` will be returned.
 *
 * @param key The key of the stored value
 * @param storageType (optional) The storage type to use ('local' or 'session'. Default is 'local')
 * @return The stored value, parsed as JSON, or null if no value is stored or an exception occurs
 *         when retrieving the value
 */
const _getStorageValue = (key, storageType) => {
	try {
		const rawValue = _getStorageObject(storageType).getItem(key);
		if (rawValue) {
			return JSON.parse(rawValue);
		}
	} catch (e) {}
	return null;
};

/**
 * Store a value in the desired storage object.
 * The value will be converted to a String using JSON.stringify.
 *
 * @param key The key of the value to store
 * @param value The value or object to store
 * @param storageType (optional) The storage type to use ('local' or 'session'. Default is 'local')
 * @return true if the value was stored without exception, false if an exception occurred
 */
const _setStorageValue = (key, value, storageType) => {
	try {
		_getStorageObject(storageType).setItem(key, JSON.stringify(value));
		return true;
	} catch (e) {}
	return false;
};

/**
 * Remove a value from storage.
 *
 * @param key The key of the value to remove
 * @param storageType (optional) The storage type to use ('local' or 'session'. Default is 'local')
 * @return true if the value was removed without exception, false if an exception occurred
 */
const _removeStorageKey = (key, storageType) => {
	try {
		_getStorageObject(storageType).removeItem(key);
		return true;
	} catch (e) {}
	return false;
};

export const getLocalStorage = (key) => {
	return _getStorageValue(key);
};

export const setLocalStorage = (key, value) => {
	return _setStorageValue(key, value);
};

export const removeLocalStorage = (key) => {
	return _removeStorageKey(key);
};

export const getSessionStorage = (key) => {
	return _getStorageValue(key, 'session');
};

export const setSessionStorage = (key, value) => {
	return _setStorageValue(key, value, 'session');
};

export const removeSessionStorage = (key) => {
	return _removeStorageKey(key, 'session');
};
